/**
 * Service for handling restaurant-related operations
 */

/**
 * Format date for Google's reservation URL
 * @param {Date} date - The date to format
 * @returns {string} Formatted date string (YYYYMMDD)
 */
const formatDateForGoogle = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

/**
 * Format time for Google's reservation URL (24h format)
 * @param {string} timeStr - Time string (e.g., '19:30')
 * @returns {string} Formatted time string (HHMM)
 */
const formatTimeForGoogle = (timeStr) => {
  if (!timeStr) return '';
  return timeStr.replace(':', '');
};

/**
 * Check if an activity is a restaurant
 * @param {Object} activity - The activity to check
 * @returns {boolean} True if the activity is a restaurant
 */
export const isRestaurant = (activity) => {
  if (!activity) return false;
  
  // Check if type is explicitly set to 'restaurant'
  if (activity.type === 'restaurant') return true;
  
  // Check common restaurant keywords in title or description
  const restaurantKeywords = [
    'restaurant', 'cafe', 'bistro', 'eatery', 'diner', 'grill', 'steakhouse',
    'pizzeria', 'trattoria', 'ristorante', 'brasserie', 'taverna', 'tasca',
    'comida', 'jantar', 'almoço', 'jantar', 'ceia', 'jantar'
  ];
  
  const searchText = `${activity.title || ''} ${activity.desc || ''}`.toLowerCase();
  return restaurantKeywords.some(keyword => searchText.includes(keyword));
};

/**
 * Generate a Google reservation link with prefilled details
 * @param {Object} options - Reservation options
 * @param {string} options.restaurantName - Name of the restaurant
 * @param {string} [options.location=''] - Location/city of the restaurant
 * @param {Date} [options.date] - Reservation date
 * @param {string} [options.time] - Reservation time (e.g., '19:30')
 * @param {number} [options.partySize=2] - Number of people
 * @returns {string} Google search URL with reservation parameters
 */
export const getGoogleReservationLink = ({
  restaurantName,
  location = '',
  date = new Date(),
  time = '20:00',
  partySize = 2
}) => {
  const baseUrl = 'https://www.google.com/search';
  const params = new URLSearchParams({
    q: `${restaurantName} ${location} reservas`,
    ibp: 'htl;restaurants'
  });

  // Add reservation parameters if date is provided
  if (date) {
    params.append('date', formatDateForGoogle(new Date(date)));
    params.append('time', formatTimeForGoogle(time));
    params.append('partySize', partySize.toString());
  }

  return `${baseUrl}?${params.toString()}`;
};

/**
 * Get reservation time based on slot key (morning, lunch, etc.)
 * @param {string} slotKey - The time slot key
 * @returns {string} Default reservation time for the slot
 */
export const getDefaultReservationTime = (slotKey) => {
  const timeMap = {
    morning: '10:00',
    lunch: '13:00',
    afternoon: '16:00',
    dinner: '20:00',
    evening: '22:00'
  };
  return timeMap[slotKey] || '20:00';
};

export default {
  isRestaurant,
  getGoogleReservationLink,
  getDefaultReservationTime
};
